import * as React from 'react';
import { Link } from 'react-router-dom';

import {
  DataTable,
  StatusChip,
  type DataTableState,
  type IDataColumn,
  type IDataGrouping,
  openRow,
  RowOpens,
  usePanePrefs,
  useOpenRecord
} from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IMyListTask, IMyTask, MyTaskType } from '../../api/work-item-contracts';
import {
  CardLink,
  personHubPath,
  recordSurveyPath,
  type ICardLinkState
} from '../../components/participant-link';
import { callOverdue, ddNextStep, ddStatusChip, meetingLabel } from '../../dd';
import { checkNumberText, checkReasonLabel } from '../../number-checks';
import {
  myCheckTaskPath,
  myListTaskKey,
  myListTaskPath,
  myMeetingTaskPath,
  myTaskPath,
  numberCheckKey
} from '../../paths';
import { ddAnchor, type IDdRevealState } from '../person-hub/due-diligence';
import { DdOverdueChip } from '../dd/dd-parts';
import { RowNameClicks } from '../../components/row-name-click';
import { FlagMarks, NewMark, TodayChip, markRowProps, todayShown } from './task-marks';
import { Due, WorkItemStateChip } from './work-items-table';

/**
 * Everything assigned to the viewer, in one table (SR-424, LST-11): a compliance work item, a
 * number check (SR-487, ruling 16) and a Lists row are three models survey-api keeps apart
 * (due-diligence design D6, PFR §4.1). **Each row says what to do next, in its own type's words**
 * — a work item's subject, a check's number, a DD call's next step on the cadence (`ddNextStep`,
 * from the row's own status and outreach), a reinstatement's reason, "Assigned in <view>" for any
 * other row — the saved view it was assigned from, "Lists" where none — then whom it concerns,
 * its state and its date.
 * Unfiltered, the rows of every type are grouped under their type, each heading carrying survey-api's count of
 * the whole type, not of the page.
 *
 * **Every row opens the same way**, beside My tasks with the list kept in the split view's pane: a
 * work item in the queue's pane, a check in the Number checks pane (`AdminNumberCheckPage`), a
 * Lists row on its task page (`AdminMyListTaskPage`), where its work is done. The person is a link
 * of their own, to their record — a DD call's to its Due diligence block.
 *
 * The type is survey-api's (`MyTaskTypes`): a row is a DD call because its survey's seeded *DD
 * list* holds it now, never because this side read its cells. So are a row's `flags`, marked on it
 * (`task-marks.tsx`): overdue, due today, new since the viewer's last visit, and the flags the list
 * is narrowed to.
 */

/** The task types in words, in the order the filter offers them. */
export const MY_TASK_TYPES: ReadonlyArray<{ value: MyTaskType; label: string }> = [
  { value: 'compliance', label: 'Compliance queue' },
  { value: 'identity_check', label: 'Number checks' },
  { value: 'meeting', label: 'DD meetings' },
  { value: 'dd_call', label: 'DD calls' },
  { value: 'reinstatement', label: 'Reinstatement' },
  { value: 'metrics', label: 'Metrics updates' },
  { value: 'list', label: 'Lists' }
];

const TYPE_WORD: Record<MyTaskType, string> = {
  compliance: 'Compliance',
  identity_check: 'Number check',
  meeting: 'DD meeting',
  dd_call: 'DD call',
  reinstatement: 'Reinstatement',
  metrics: 'Metrics update',
  list: 'Lists'
};

/** A task's type in words, one this table lacks as spelled. */
export function typeWord(type: string): string {
  return TYPE_WORD[type as MyTaskType] ?? type;
}

/** Where a task opens: beside My tasks, a work item's pane, a check's or a Lists row's page. */
export function taskPath(task: IMyTask): string {
  if (task.workItem) return myTaskPath(task.workItem.id);
  if (task.check) return myCheckTaskPath(task.check.participant.participantId, task.check.checkId);
  if (task.meeting) return myMeetingTaskPath(task.meeting.interviewId);
  return myListTaskPath(task.row!.participantId, task.row!.survey);
}

/** A task's key — the split view's record key: the work item's id, the check's or the pair's. */
export function taskKey(task: IMyTask): string {
  if (task.workItem) return task.workItem.id;
  if (task.check) return numberCheckKey(task.check.participant.participantId, task.check.checkId);
  if (task.meeting) return task.meeting.interviewId;
  return myListTaskKey(task.row!.participantId, task.row!.survey);
}

/** Whom a task concerns: a work item's or a check's participant, a Lists row's person. */
export function taskName(task: IMyTask): string {
  if (task.workItem) return task.workItem.participant.displayName;
  if (task.meeting) return task.meeting.displayName;
  return task.check ? task.check.participant.displayName : task.row!.displayName;
}

/** The survey a task is on — none for a check, which is about a person's number. */
export function taskSurvey(task: IMyTask): Nullable<string> {
  if (task.workItem) return task.workItem.surveyCode;
  if (task.meeting) return task.meeting.survey;
  return task.check ? null : task.row!.survey;
}

/** What to do next, in the task's own type's words. */
export function taskTitle(task: IMyTask): string {
  if (task.workItem) return task.workItem.subject;
  if (task.check) return `Number check · ${checkNumberText(task.check)}`;
  if (task.meeting) return meetingLabel(task.meeting.kind);
  const row = task.row!;
  if (task.type === 'dd_call') {
    const step = ddNextStep(row.ddStatus ?? 'none', row.facts.ddOutreach ?? 0, row.facts.ddScheduledAt);
    // Who booked the call, so nobody emails the advisor again (SR-424).
    const booked = row.facts.ddScheduledAt && row.ddStatus !== 'completed';
    const by = booked && row.facts.ddScheduledBy ? ` set by ${row.facts.ddScheduledBy}` : '';
    return `DD call · ${step}${by}`;
  }
  if (task.type === 'reinstatement') {
    return `Reconsider · ${row.facts.reason?.label ?? 'legacy Travel 2 or 3'}`;
  }
  if (task.type === 'metrics') {
    const sent = row.facts.metricsRequests ?? 0;
    return `Figures owed · ${sent === 0 ? 'no request yet' : `${sent} ${sent === 1 ? 'request' : 'requests'} sent`}`;
  }
  return `Assigned in ${row.assignedIn?.name ?? 'Lists'}`;
}

export function MyTasksTable({
  rows,
  state,
  search,
  grouped,
  counts,
  flags = [],
  stickyTop
}: {
  rows: IMyTask[];
  state?: DataTableState;
  /** My tasks' own query, kept in each task link's history state, so it opens beside the list. */
  search: string;
  /** Group the rows under their type — the list of every type. */
  grouped: boolean;
  /** survey-api's count of each type, said on its heading. */
  counts: Nullable<Record<string, number>>;
  /** The flags the list is narrowed to, each mark drawn on the rows raising it. */
  flags?: readonly string[];
  /** The page scrolls, the header sticking this far under the window's top (`DataTable`'s). */
  stickyTop?: string;
}) {
  // Beside the list as *Opens in* says — its column, or a sheet over this page (SR-530).
  const openRecord = useOpenRecord();
  const linkState: ICardLinkState = { search, list: true };
  // The task beside the list, or its page in a new tab, by *Opens in* (SR-530); both ways in at the
  // row's start.
  const prefs = usePanePrefs();
  const beside = (task: IMyTask) => openRecord(taskPath(task), linkState);
  const today = new Date().toISOString().slice(0, 10);
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(new Set());
  const order = MY_TASK_TYPES.map((entry) => entry.value as string);
  const grouping: IDataGrouping<IMyTask> | undefined = grouped
    ? {
        levels: [
          {
            id: 'type',
            label: 'Type',
            value: (task) => task.type,
            compare: (left, right) => order.indexOf(left) - order.indexOf(right),
            display: (value) => MY_TASK_TYPES.find((entry) => entry.value === value)?.label ?? value
          }
        ],
        collapsed,
        onToggle: (key) =>
          setCollapsed((now) => {
            const next = new Set(now);
            if (!next.delete(key)) next.add(key);
            return next;
          }),
        count: ([type]) => (counts?.[type] !== undefined ? `(${counts[type]})` : null)
      }
    : undefined;

  const columns: IDataColumn<IMyTask>[] = [
    {
      id: 'open',
      header: <span className="sr-only">Open</span>,
      label: 'Open',
      className: 'w-0 whitespace-nowrap',
      cell: (task) => (
        <RowOpens
          name={taskTitle(task)}
          onPane={() => beside(task)}
          to={taskPath(task)}
          tabWords={`Open ${taskTitle(task)} in a new tab`}
        />
      )
    },
    {
      id: 'task',
      header: 'To do',
      cell: (task) => (
        <>
          <Link
            to={taskPath(task)}
            state={linkState}
            data-task-link=""
            data-task-type={task.type}
            className="font-medium text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            {taskTitle(task)}
          </Link>
          {task.flags?.isNew && <NewMark />}
          <FlagMarks narrowed={flags} flags={task.flags} />
          {task.meeting?.scheduleNote && (
            <span className="block text-xs whitespace-pre-wrap text-muted-foreground">
              {task.meeting.scheduleNote}
            </span>
          )}
        </>
      )
    },
    {
      id: 'participant',
      header: 'Advisor or team',
      cell: (task) => <Concerns task={task} />
    },
    {
      id: 'survey',
      header: 'Survey',
      className: 'text-xs whitespace-nowrap',
      cell: (task) =>
        task.workItem ? (
          task.workItem.surveyCode
        ) : task.row ? (
          `${task.row.survey} · ${task.row.cycle}`
        ) : task.meeting ? (
          `${task.meeting.survey} · ${task.meeting.cycle}`
        ) : (
          <span className="text-muted-foreground">—</span>
        )
    },
    {
      id: 'state',
      header: 'State',
      cell: (task) => <TaskState task={task} />
    },
    {
      id: 'when',
      header: 'Due or last move',
      className: 'text-xs whitespace-nowrap',
      cell: (task) =>
        task.workItem ? (
          <Due dueOn={task.workItem.dueOn} today={today} dueToday={task.flags?.dueToday} />
        ) : (
          <RowWhen task={task} />
        )
    }
  ];

  return (
    // A plain click on the task's link follows *Opens in* too (`RowNameClicks`).
    <RowNameClicks selector="a[data-task-link]" open={(to) => openRecord(to, linkState)}>
      <DataTable
        columns={columns}
        rows={rows.length === 0 ? null : rows}
        rowKey={taskKey}
        rowProps={(task) =>
          ({
            'data-my-task': taskKey(task),
            ...markRowProps(task.flags)
          }) as React.HTMLAttributes<HTMLTableRowElement>
        }
        onOpen={(task) => openRow(prefs, taskPath(task), () => beside(task))}
        state={state}
        pickerId="my-tasks-all"
        grouping={grouping}
        stickyTop={stickyTop}
      />
    </RowNameClicks>
  );
}

function Concerns({ task }: { task: IMyTask }) {
  if (task.check) {
    const { participant } = task.check;
    return (
      <CardLink to={personHubPath(participant.participantId)}>{participant.displayName}</CardLink>
    );
  }
  if (task.workItem) {
    const { participant } = task.workItem;
    return (
      <span className="grid">
        <CardLink to={personHubPath(participant.id)}>{participant.displayName}</CardLink>
        {participant.firmName && (
          <span className="text-xs text-muted-foreground">{participant.firmName}</span>
        )}
      </span>
    );
  }
  if (task.meeting) {
    return (
      <span className="grid">
        <RecordLink type="dd_call" row={task.meeting} />
        {task.meeting.firmName && (
          <span className="text-xs text-muted-foreground">{task.meeting.firmName}</span>
        )}
      </span>
    );
  }
  const row = task.row!;
  return (
    <span className="grid">
      <RecordLink type={task.type} row={row} />
      {row.firmName && <span className="text-xs text-muted-foreground">{row.firmName}</span>}
    </span>
  );
}

/** A Lists row's or a meeting's person, to the record's survey tab — a DD call's or a meeting's
 *  landing on its Due diligence block. */
export function RecordLink({
  type,
  row
}: {
  type: MyTaskType;
  row: Pick<IMyListTask, 'participantId' | 'survey' | 'displayName'>;
}) {
  const state: IDdRevealState | undefined =
    type === 'dd_call' ? { revealDd: ddAnchor(row.participantId, row.survey) } : undefined;
  return (
    <Link
      to={recordSurveyPath(row.participantId, row.survey)}
      state={state}
      className="font-medium text-[var(--color-sapphire)] underline-offset-4 hover:underline"
    >
      {row.displayName}
    </Link>
  );
}

/** The state a task is in, by its type: a work item's, why a check's number waits, a DD call's
 *  status, a reinstatement's handling, a list row's response status. */
export function TaskState({ task }: { task: IMyTask }) {
  if (task.workItem) return <WorkItemStateChip state={task.workItem.state} />;
  if (task.check) return <span className="text-xs">{checkReasonLabel(task.check.reason)}</span>;
  if (task.meeting) {
    const chip = ddStatusChip(task.meeting.status);
    return <StatusChip tone={chip.tone}>{chip.label}</StatusChip>;
  }
  const row = task.row!;
  if (task.type === 'dd_call') {
    const chip = ddStatusChip(row.ddStatus ?? 'none');
    return <StatusChip tone={chip.tone}>{chip.label}</StatusChip>;
  }
  const said = task.type === 'reinstatement' ? row.facts.handling : row.facts.status;
  return said ? (
    <span className="text-xs">{said.label}</span>
  ) : (
    <span className="text-xs text-muted-foreground">—</span>
  );
}

/** A Lists row's latest move: a DD call's last outreach, else when it was assigned — and when a
 *  check was. */
function RowWhen({ task }: { task: IMyTask }) {
  if (task.check) {
    return (
      <span className="text-muted-foreground">
        Assigned {formatDateTime(task.check.assignedAt ?? task.check.createdAt)}
      </span>
    );
  }
  if (task.meeting) {
    const { scheduledAt, status } = task.meeting;
    // The meeting's booking is when it is due; past, overdue — the DD calls' tone.
    const overdue = callOverdue(scheduledAt, status) || task.flags?.overdue === true;
    return scheduledAt ? (
      <span className="inline-flex items-center gap-1.5" data-dd-scheduled={scheduledAt}>
        {formatDateTime(scheduledAt)}
        {overdue && <DdOverdueChip />}
        {todayShown(overdue, task.flags?.dueToday, scheduledAt) && <TodayChip />}
      </span>
    ) : (
      <span className="text-muted-foreground">Not scheduled</span>
    );
  }
  const row = task.row!;
  if (task.type === 'dd_call') {
    // A booked call is when the task is due (SR-424); past, it is overdue — the work items' tone.
    if (row.facts.ddScheduledAt && row.ddStatus !== 'completed') {
      const overdue = callOverdue(row.facts.ddScheduledAt, row.ddStatus) || task.flags?.overdue === true;
      return (
        <span className="inline-flex items-center gap-1.5" data-dd-scheduled={row.facts.ddScheduledAt}>
          Call {formatDateTime(row.facts.ddScheduledAt)}
          {overdue && <DdOverdueChip />}
          {todayShown(overdue, task.flags?.dueToday, row.facts.ddScheduledAt) && <TodayChip />}
        </span>
      );
    }
    return row.facts.ddLastOutreachAt ? (
      <span>Last outreach {formatDateTime(row.facts.ddLastOutreachAt)}</span>
    ) : (
      <span className="text-muted-foreground">No outreach yet</span>
    );
  }
  if (task.type === 'metrics' && row.facts.metricsLastRequestAt) {
    return <span>Last request {formatDateTime(row.facts.metricsLastRequestAt)}</span>;
  }
  return <span className="text-muted-foreground">Assigned {formatDateTime(row.assignedAt)}</span>;
}
